/**
 * TrackCollaboratorIndicator Component
 * Shows which collaborators are currently editing a track
 */

import React, { memo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

// Single collaborator avatar
const CollaboratorAvatar = memo(({ user, size = 'sm' }) => {
  const sizeClasses = {
    xs: 'w-4 h-4 text-[8px]',
    sm: 'w-5 h-5 text-[10px]',
    md: 'w-6 h-6 text-xs',
  };
  
  return (
    <motion.div
      initial={{ scale: 0, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      exit={{ scale: 0, opacity: 0 }}
      className={`${sizeClasses[size]} rounded-full flex items-center justify-center font-medium text-white ring-2 ring-zinc-900`}
      style={{ backgroundColor: user.color }}
      title={`${user.name} is editing this track`}
    >
      {user.name?.charAt(0)?.toUpperCase() || '?'}
    </motion.div>
  );
});

CollaboratorAvatar.displayName = 'CollaboratorAvatar';

// Track collaborator indicator - shows avatars of users editing this track
export const TrackCollaboratorIndicator = memo(({ 
  trackId, 
  users = [], 
  currentUserId,
  position = 'right', // 'left', 'right', 'top'
}) => {
  // Filter users who are editing this specific track (excluding current user)
  const editingUsers = users.filter(
    user => user.selectedTrack === trackId && user.id !== currentUserId
  );
  
  if (editingUsers.length === 0) return null;
  
  const positionClasses = {
    left: 'absolute left-0 top-1/2 -translate-y-1/2 -translate-x-1/2 flex flex-col gap-1',
    right: 'absolute right-2 top-1/2 -translate-y-1/2 flex flex-col gap-1',
    top: 'absolute top-0 right-2 -translate-y-1/2 flex gap-1',
  };
  
  return (
    <div 
      className={positionClasses[position]}
      data-testid={`track-collaborators-${trackId}`}
    >
      <AnimatePresence mode="popLayout">
        {editingUsers.slice(0, 3).map(user => (
          <CollaboratorAvatar key={user.id} user={user} size="sm" />
        ))}
        {editingUsers.length > 3 && (
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            className="w-5 h-5 rounded-full bg-zinc-700 flex items-center justify-center text-[10px] text-zinc-300"
          >
            +{editingUsers.length - 3}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
});

TrackCollaboratorIndicator.displayName = 'TrackCollaboratorIndicator';

// Track selection border - colored border when another user is editing
export const TrackSelectionBorder = memo(({ 
  trackId, 
  users = [], 
  currentUserId,
}) => {
  // Find the first user editing this track (excluding current user)
  const editingUser = users.find(
    user => user.selectedTrack === trackId && user.id !== currentUserId
  );
  
  if (!editingUser) return null;
  
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="absolute inset-0 pointer-events-none rounded-lg"
      style={{
        boxShadow: `inset 0 0 0 2px ${editingUser.color}40, 0 0 8px ${editingUser.color}30`,
        borderLeft: `3px solid ${editingUser.color}`,
      }}
      data-testid={`track-selection-border-${trackId}`}
    />
  );
});

TrackSelectionBorder.displayName = 'TrackSelectionBorder';

// Editing indicator tooltip
export const EditingIndicatorTooltip = memo(({ 
  trackId, 
  users = [], 
  currentUserId,
}) => {
  const editingUsers = users.filter(
    user => user.selectedTrack === trackId && user.id !== currentUserId
  );
  
  if (editingUsers.length === 0) return null;
  
  const names = editingUsers.map(u => u.name).join(', ');
  const verb = editingUsers.length === 1 ? 'is' : 'are';
  
  return (
    <motion.div
      initial={{ opacity: 0, y: -5 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -5 }}
      className="absolute -top-6 left-1/2 -translate-x-1/2 px-2 py-1 bg-zinc-800 rounded text-xs text-zinc-300 whitespace-nowrap z-50 shadow-lg"
      style={{ borderTop: `2px solid ${editingUsers[0]?.color || '#6366f1'}` }}
    >
      {names} {verb} editing
    </motion.div>
  );
});

EditingIndicatorTooltip.displayName = 'EditingIndicatorTooltip';

export default TrackCollaboratorIndicator;
